import React, { memo, useMemo } from 'react';
import { useTheme, Button } from 'tamagui';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import { CobuStack, CobuText } from '@cobu/ui';
import { useGroupMembers } from '../../hooks/useGroupMembers';
import { GroupMembersProps, GROUP_MEMBERS_CONSTANTS } from '@cobu/types';
import { Strings, URLS } from '../../constants';
import MemberAvatar from './MemberAvatar';
import MemberOverflow from './MemberOverflow';
import GroupMembersShimmer from './GroupMembersShimmer';

const GroupMembers: React.FC<GroupMembersProps> = memo(({
  // External data (optional)
  members: externalMembers,
  totalCount: externalTotalCount,

  // Context configuration
  buildingId,
  groupId,
  isGlobalChat = false,
  isWelcomeMode = false,

  // Display configuration
  maxDisplay = isWelcomeMode
    ? GROUP_MEMBERS_CONSTANTS.WELCOME_MAX_DISPLAY
    : GROUP_MEMBERS_CONSTANTS.DEFAULT_MAX_DISPLAY,
  size = GROUP_MEMBERS_CONSTANTS.DEFAULT_AVATAR_SIZE,
  showViewAllButton = true,

  // Loading states
  loading: externalLoading,
  error: externalError,

  // Callbacks
  onShowAllMembers,
  onMemberPress,
  onRefresh,

  // Customization
  // emptyState,
  // loadingState,


}) => {
  const theme = useTheme();
  const strings = Strings.get().groupMembers;

  const hasProfilePicture = (m: any) =>
    !!m?.profileImage && m.profileImage !== URLS.DEFAULT_PROFILE_IMAGE;

  // Use hook for data fetching (if external data not provided)
  const hookData = useGroupMembers({
    buildingId: externalMembers ? undefined : buildingId,
    groupId: externalMembers ? undefined : groupId,
    enabled: !externalMembers,
    maxDisplay,
    prioritizePictures: true,
    filterResidentsOnly: true,
  });

  // Use external data if provided, otherwise use hook data
  // Total count first
  const totalCount =
    externalTotalCount !== undefined ? externalTotalCount : hookData.totalCount;

  // Use external data if provided (photos first + reserve one for bubble), otherwise hook data
  const displayMembers = useMemo(() => {
    if (externalMembers && externalMembers.length) {
      const withPic = externalMembers.filter(hasProfilePicture);
      const withoutPic = externalMembers.filter(m => !hasProfilePicture(m));
      const ordered = withPic.concat(withoutPic);
      const baseCap = isWelcomeMode
        ? GROUP_MEMBERS_CONSTANTS.WELCOME_MAX_DISPLAY
        : maxDisplay;
      const hasOverflowLocal = totalCount > baseCap;
      const cap = hasOverflowLocal ? baseCap - 1 : baseCap; // reserve 1 for "+N"
      return ordered.slice(0, Math.max(0, cap));
    }
    return hookData.displayMembers;
  }, [externalMembers, hookData.displayMembers, isWelcomeMode, maxDisplay, totalCount]);
  const isLoading = externalLoading !== undefined ? externalLoading : hookData.isLoading;
  const isError = !!externalError || hookData.isError;
  const hasOverflow = totalCount > displayMembers.length;
  const overflowCount = totalCount - displayMembers.length;

  // Memoized member list rendering
  const memberList = useMemo(() => {
    return displayMembers.map((member, index) => (
      <MemberAvatar
        key={member.uuid}
        member={member}
        index={index}
        size={size}
        isGlobalChat={isGlobalChat}
        onPress={onMemberPress}
      />
    ));
  }, [displayMembers, size, isGlobalChat, onMemberPress]);

  // Memoized overflow indicator
  const overflowIndicator = useMemo(() => {
    if (!hasOverflow || overflowCount <= 0) return null;

    return (
      <MemberOverflow
        count={overflowCount}
        index={displayMembers.length}
        size={size}
        isGlobalChat={isGlobalChat}
        onPress={onShowAllMembers}
      />
    );
  }, [hasOverflow, overflowCount, displayMembers.length, size, isGlobalChat, onShowAllMembers]);

  // Handle refresh action

  // Loading state - show shimmer while loading
  if (isLoading) {
    return <GroupMembersShimmer />;
  }

  // Error state
  if (isError && displayMembers.length === 0) {
    return (
      null
    );
  }

  // Empty state
  if (displayMembers.length === 0) {
    return null;
  }

  return (
    <CobuStack layout='horizontal'
      ai="center"
      jc="space-between"
      p="$4"
    >
      {/* Member avatars row */}
      <CobuStack layout='horizontal' ai="center" f={1}>
        {memberList}
        {overflowIndicator}
      </CobuStack>

      {/* View All Members button */}
      {showViewAllButton && !isWelcomeMode && onShowAllMembers && (
        <Button
          unstyled
          bg="transparent"
          p={0}
          cursor="pointer"
          hoverStyle={{ bg: 'transparent' }}
          pressStyle={{ bg: 'transparent' }}
          focusStyle={{ outlineWidth: 0 }}
          onPress={onShowAllMembers}
        >
          <CobuStack layout='horizontal' ai="center" gap="$1" ml="$4">
            <CobuText variant="body" color="$textLinkColor">{strings.viewAll}</CobuText>
            <ChevronRightIcon
              style={{ width: GROUP_MEMBERS_CONSTANTS.CHEVRON_ICON_SIZE, height: GROUP_MEMBERS_CONSTANTS.CHEVRON_ICON_SIZE, color: theme.textLinkColor?.val }}
            />
          </CobuStack>
        </Button>
      )}
    </CobuStack>
  );
});

GroupMembers.displayName = 'GroupMembers';

export default GroupMembers; 